import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Tag,
  Plus,
  ArrowLeft,
  Loader2,
  Check,
  X,
  Calendar,
  Percent,
  DollarSign,
  Users,
  CreditCard,
  ShoppingCart,
  History,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
  Eye,
  Pencil,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scopeOptions = [
  { value: 'order', label: 'Commande entière', icon: ShoppingCart },
  { value: 'cards', label: 'Cartes uniquement', icon: CreditCard },
  { value: 'subscription', label: 'Abonnement uniquement', icon: Calendar }
];

const planOptions = [
  { value: 'pro', label: 'PRO' },
  { value: 'business', label: 'BUSINESS' }
];

const cardTypeOptions = [
  { value: 'brand', label: 'Brand Card' },
  { value: 'custom', label: 'Custom Design' },
  { value: 'haiti_mondial', label: 'Haiti Mondial' },
  { value: 'enterprise', label: 'Enterprise Card' }
];

export default function AdminCoupons() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  // Create form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    scope: ['order'],
    allowed_plans: ['pro', 'business'],
    allowed_card_types: ['brand', 'custom', 'haiti_mondial', 'enterprise'],
    expires_at: '',
    max_uses: '',
    is_active: true
  });

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${API}/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCouponUsage = async (couponId) => {
    try {
      const response = await fetch(`${API}/admin/coupons/${couponId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsageHistory(data.usage_history || []);
        setSelectedCoupon(data.coupon);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    if (!newCoupon.code.trim()) {
      toast.error('Le code coupon est obligatoire');
      return;
    }
    
    if (!newCoupon.discount_value || newCoupon.discount_value <= 0) {
      toast.error('La valeur de réduction doit être supérieure à 0');
      return;
    }
    
    if (!newCoupon.expires_at) {
      toast.error('La date d\'expiration est obligatoire');
      return;
    }
    
    if (newCoupon.scope.length === 0) {
      toast.error('Veuillez sélectionner au moins une portée');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/admin/coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newCoupon,
          discount_value: parseFloat(newCoupon.discount_value),
          max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
          expires_at: new Date(newCoupon.expires_at).toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('Coupon créé avec succès');
        setShowCreateForm(false);
        setNewCoupon({
          code: '',
          discount_type: 'percentage',
          discount_value: '',
          scope: ['order'],
          allowed_plans: ['pro', 'business'],
          allowed_card_types: ['brand', 'custom', 'haiti_mondial', 'enterprise'],
          expires_at: '',
          max_uses: '',
          is_active: true
        });
        fetchCoupons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const response = await fetch(`${API}/admin/coupons/${couponId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(coupons.map(c => 
          c.coupon_id === couponId ? { ...c, is_active: data.is_active } : c
        ));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const openEditCoupon = (coupon) => {
    setEditCoupon(coupon);
    setEditForm({
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      scope: coupon.scope || ['order'],
      allowed_plans: coupon.allowed_plans || ['pro', 'business'],
      allowed_card_types: coupon.allowed_card_types || ['brand', 'custom', 'haiti_mondial', 'enterprise'],
      expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().slice(0, 16) : '',
      max_uses: coupon.max_uses || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editCoupon) return;
    setSubmitting(true);
    try {
      const body = {
        discount_type: editForm.discount_type,
        discount_value: parseFloat(editForm.discount_value),
        scope: editForm.scope,
        allowed_plans: editForm.allowed_plans,
        allowed_card_types: editForm.allowed_card_types,
        expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : undefined,
        max_uses: editForm.max_uses ? parseInt(editForm.max_uses) : null,
      };
      const response = await fetch(`${API}/admin/coupons/${editCoupon.coupon_id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        toast.success('Coupon mis à jour');
        setEditCoupon(null);
        setEditForm(null);
        fetchCoupons();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteCoupon = async (coupon) => {
    if (!window.confirm(`Supprimer le coupon "${coupon.code}" ? Cette action est irréversible.`)) return;
    setDeleting(coupon.coupon_id);
    try {
      const response = await fetch(`${API}/admin/coupons/${coupon.coupon_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Coupon supprimé');
        setCoupons(coupons.filter(c => c.coupon_id !== coupon.coupon_id));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch { toast.error('Erreur lors de la suppression'); }
    finally { setDeleting(null); }
  };

  const handleEditScopeChange = (value) => {
    setEditForm(prev => ({ ...prev, scope: prev.scope.includes(value) ? prev.scope.filter(s => s !== value) : [...prev.scope, value] }));
  };
  const handleEditPlanChange = (value) => {
    setEditForm(prev => ({ ...prev, allowed_plans: prev.allowed_plans.includes(value) ? prev.allowed_plans.filter(p => p !== value) : [...prev.allowed_plans, value] }));
  };
  const handleEditCardTypeChange = (value) => {
    setEditForm(prev => ({ ...prev, allowed_card_types: prev.allowed_card_types.includes(value) ? prev.allowed_card_types.filter(ct => ct !== value) : [...prev.allowed_card_types, value] }));
  };

  const handleScopeChange = (value) => {
    setNewCoupon(prev => ({
      ...prev,
      scope: prev.scope.includes(value)
        ? prev.scope.filter(s => s !== value)
        : [...prev.scope, value]
    }));
  };

  const handlePlanChange = (value) => {
    setNewCoupon(prev => ({
      ...prev,
      allowed_plans: prev.allowed_plans.includes(value)
        ? prev.allowed_plans.filter(p => p !== value)
        : [...prev.allowed_plans, value]
    }));
  };

  const handleCardTypeChange = (value) => {
    setNewCoupon(prev => ({
      ...prev,
      allowed_card_types: prev.allowed_card_types.includes(value)
        ? prev.allowed_card_types.filter(ct => ct !== value)
        : [...prev.allowed_card_types, value]
    }));
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0A2540] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB]">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Tag className="w-6 h-6" />
                Gestion des Coupons
              </h1>
              <p className="text-slate-600">{coupons.length} coupon(s) configuré(s)</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary rounded-xl gap-2"
            data-testid="create-coupon-btn"
          >
            <Plus className="w-4 h-4" />
            Créer un coupon
          </Button>
        </div>

        {/* Create Form Modal */}
        <AnimatePresence>
          {showCreateForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowCreateForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-xl font-bold">Créer un nouveau coupon</h2>
                  <button onClick={() => setShowCreateForm(false)}>
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                <form onSubmit={handleCreateCoupon} className="space-y-6">
                  {/* Code */}
                  <div className="space-y-2">
                    <Label>Code du coupon *</Label>
                    <Input
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                      placeholder="BIENVENUE10"
                      className="uppercase"
                      required
                    />
                    <p className="text-xs text-slate-500">Le code sera automatiquement converti en majuscules</p>
                  </div>

                  {/* Discount Type & Value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type de réduction *</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setNewCoupon({ ...newCoupon, discount_type: 'percentage' })}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                            newCoupon.discount_type === 'percentage' 
                              ? 'border-blue-500 bg-[#0A2540]/5' 
                              : 'border-slate-200'
                          }`}
                        >
                          <Percent className="w-4 h-4" />
                          Pourcentage
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCoupon({ ...newCoupon, discount_type: 'fixed' })}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                            newCoupon.discount_type === 'fixed' 
                              ? 'border-blue-500 bg-[#0A2540]/5' 
                              : 'border-slate-200'
                          }`}
                        >
                          <DollarSign className="w-4 h-4" />
                          Montant fixe
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Valeur *</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={newCoupon.discount_value}
                          onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })}
                          placeholder={newCoupon.discount_type === 'percentage' ? '10' : '5'}
                          min="0.01"
                          step="0.01"
                          className="pr-10"
                          required
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                          {newCoupon.discount_type === 'percentage' ? '%' : '$'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Scope */}
                  <div className="space-y-2">
                    <Label>Portée de la réduction *</Label>
                    <div className="flex flex-wrap gap-2">
                      {scopeOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleScopeChange(opt.value)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            newCoupon.scope.includes(opt.value)
                              ? 'border-blue-500 bg-[#0A2540]/5 text-[#0A2540]'
                              : 'border-slate-200 text-slate-600'
                          }`}
                        >
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                          {newCoupon.scope.includes(opt.value) && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Plans */}
                  <div className="space-y-2">
                    <Label>Plans autorisés</Label>
                    <div className="flex gap-2">
                      {planOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handlePlanChange(opt.value)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all ${
                            newCoupon.allowed_plans.includes(opt.value)
                              ? 'border-green-500 bg-[#00C48C]/5 text-[#00A67E]'
                              : 'border-slate-200 text-slate-600'
                          }`}
                        >
                          {opt.label}
                          {newCoupon.allowed_plans.includes(opt.value) && <Check className="w-4 h-4 inline ml-2" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Card Types */}
                  <div className="space-y-2">
                    <Label>Types de cartes autorisés</Label>
                    <div className="flex flex-wrap gap-2">
                      {cardTypeOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleCardTypeChange(opt.value)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                            newCoupon.allowed_card_types.includes(opt.value)
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-slate-200 text-slate-600'
                          }`}
                        >
                          {opt.label}
                          {newCoupon.allowed_card_types.includes(opt.value) && <Check className="w-3 h-3 inline ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Expiration & Max Uses */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date d'expiration *</Label>
                      <Input
                        type="datetime-local"
                        value={newCoupon.expires_at}
                        onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre max d'utilisations</Label>
                      <Input
                        type="number"
                        value={newCoupon.max_uses}
                        onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })}
                        placeholder="Illimité"
                        min="1"
                      />
                      <p className="text-xs text-slate-500">Laisser vide pour illimité</p>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Important</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Les coupons ne sont valables qu'avec le paiement automatique (Stripe)</li>
                        <li>1 seule utilisation par client</li>
                        <li>Les coupons ne peuvent pas être supprimés, seulement désactivés</li>
                      </ul>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 rounded-xl"
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 btn-primary rounded-xl"
                      data-testid="submit-coupon-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Création...
                        </>
                      ) : (
                        'Créer le coupon'
                      )}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Usage History Modal */}
        <AnimatePresence>
          {selectedCoupon && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => { setSelectedCoupon(null); setUsageHistory([]); }}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold">Historique d'utilisation</h2>
                    <p className="text-slate-600">Coupon: <span className="font-mono font-bold">{selectedCoupon.code}</span></p>
                  </div>
                  <button onClick={() => { setSelectedCoupon(null); setUsageHistory([]); }}>
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                {usageHistory.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune utilisation pour ce coupon</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usageHistory.map((usage, i) => (
                      <div key={usage.usage_id || i} className="bg-slate-50 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">
                              Commande: <span className="font-mono">{usage.order_id}</span>
                            </p>
                            <p className="text-sm text-slate-500">
                              Client: {usage.user_id}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#00C48C]">-${usage.discount_amount?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(usage.used_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Coupon Modal */}
        <AnimatePresence>
          {editCoupon && editForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => { setEditCoupon(null); setEditForm(null); }}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold">Modifier le coupon</h2>
                    <p className="text-slate-600">Code: <span className="font-mono font-bold">{editCoupon.code}</span> &middot; Utilisations: <span className="font-bold">{editCoupon.current_uses || 0}{editCoupon.max_uses ? ` / ${editCoupon.max_uses}` : ''}</span></p>
                  </div>
                  <button onClick={() => { setEditCoupon(null); setEditForm(null); }}><X className="w-5 h-5 text-slate-400" /></button>
                </div>

                <form onSubmit={handleSaveEdit} className="space-y-5">
                  {/* Discount Type & Value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type de réduction</Label>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setEditForm({ ...editForm, discount_type: 'percentage' })}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${editForm.discount_type === 'percentage' ? 'border-blue-500 bg-[#0A2540]/5' : 'border-slate-200'}`}>
                          <Percent className="w-4 h-4" /> Pourcentage
                        </button>
                        <button type="button" onClick={() => setEditForm({ ...editForm, discount_type: 'fixed' })}
                          className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${editForm.discount_type === 'fixed' ? 'border-blue-500 bg-[#0A2540]/5' : 'border-slate-200'}`}>
                          <DollarSign className="w-4 h-4" /> Montant fixe
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Valeur</Label>
                      <div className="relative">
                        <Input type="number" value={editForm.discount_value} onChange={e => setEditForm({ ...editForm, discount_value: e.target.value })} min="0.01" step="0.01" className="pr-10" required />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{editForm.discount_type === 'percentage' ? '%' : '$'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Scope */}
                  <div className="space-y-2">
                    <Label>Portée</Label>
                    <div className="flex flex-wrap gap-2">
                      {scopeOptions.map(opt => (
                        <button key={opt.value} type="button" onClick={() => handleEditScopeChange(opt.value)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${editForm.scope.includes(opt.value) ? 'border-blue-500 bg-[#0A2540]/5 text-[#0A2540]' : 'border-slate-200 text-slate-600'}`}>
                          <opt.icon className="w-4 h-4" /> {opt.label}
                          {editForm.scope.includes(opt.value) && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Plans */}
                  <div className="space-y-2">
                    <Label>Plans autorisés</Label>
                    <div className="flex gap-2">
                      {planOptions.map(opt => (
                        <button key={opt.value} type="button" onClick={() => handleEditPlanChange(opt.value)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all ${editForm.allowed_plans.includes(opt.value) ? 'border-green-500 bg-[#00C48C]/5 text-[#00A67E]' : 'border-slate-200 text-slate-600'}`}>
                          {opt.label} {editForm.allowed_plans.includes(opt.value) && <Check className="w-4 h-4 inline ml-2" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Card Types */}
                  <div className="space-y-2">
                    <Label>Types de cartes</Label>
                    <div className="flex flex-wrap gap-2">
                      {cardTypeOptions.map(opt => (
                        <button key={opt.value} type="button" onClick={() => handleEditCardTypeChange(opt.value)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all text-sm ${editForm.allowed_card_types.includes(opt.value) ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 text-slate-600'}`}>
                          {opt.label} {editForm.allowed_card_types.includes(opt.value) && <Check className="w-3 h-3 inline ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Expiration & Max Uses */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date d'expiration</Label>
                      <Input type="datetime-local" value={editForm.expires_at} onChange={e => setEditForm({ ...editForm, expires_at: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre max d'utilisations</Label>
                      <Input type="number" value={editForm.max_uses} onChange={e => setEditForm({ ...editForm, max_uses: e.target.value })} placeholder="Illimité" min="1" />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setEditCoupon(null); setEditForm(null); }} className="flex-1 rounded-xl">Annuler</Button>
                    <Button type="submit" disabled={submitting} className="flex-1 btn-primary rounded-xl" data-testid="save-edit-coupon-btn">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde...</> : 'Sauvegarder'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Coupons List */}
        {coupons.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-soft p-12 text-center"
          >
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucun coupon configuré</h2>
            <p className="text-slate-600 mb-6">Créez votre premier coupon de réduction</p>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un coupon
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-soft overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Code</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Réduction</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Portée</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Restrictions</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Utilisations</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Expiration</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Statut</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {coupons.map((coupon) => (
                    <tr key={coupon.coupon_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className="font-mono font-bold text-[#0A2540] bg-[#0A2540]/5 px-2 py-1 rounded">
                          {coupon.code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {coupon.discount_type === 'percentage' ? (
                            <Percent className="w-4 h-4 text-slate-400" />
                          ) : (
                            <DollarSign className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="font-medium">
                            {coupon.discount_value}
                            {coupon.discount_type === 'percentage' ? '%' : '$'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {coupon.scope?.map(s => (
                            <span key={s} className="text-xs bg-slate-100 px-2 py-1 rounded">
                              {s === 'order' ? 'Commande' : s === 'cards' ? 'Cartes' : 'Abo'}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs space-y-1">
                          <div className="flex flex-wrap gap-1">
                            {coupon.allowed_plans?.map(p => (
                              <span key={p} className="bg-[#00C48C]/10 text-[#00A67E] px-1 rounded">
                                {p.toUpperCase()}
                              </span>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {coupon.allowed_card_types?.slice(0, 2).map(ct => (
                              <span key={ct} className="bg-purple-100 text-purple-700 px-1 rounded">
                                {ct}
                              </span>
                            ))}
                            {coupon.allowed_card_types?.length > 2 && (
                              <span className="text-slate-400">+{coupon.allowed_card_types.length - 2}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-bold ${
                            coupon.max_uses && coupon.current_uses >= coupon.max_uses 
                              ? 'bg-red-50 text-red-600' 
                              : (coupon.current_uses || 0) > 0
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-slate-50 text-slate-500'
                          }`} data-testid={`usage-count-${coupon.coupon_id}`}>
                            <Users className="w-3.5 h-3.5" />
                            {coupon.current_uses || 0}
                            {coupon.max_uses ? ` / ${coupon.max_uses}` : ' / \u221e'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${isExpired(coupon.expires_at) ? 'text-red-600' : 'text-slate-600'}`}>
                          {new Date(coupon.expires_at).toLocaleDateString('fr-FR')}
                        </span>
                        {isExpired(coupon.expires_at) && (
                          <span className="block text-xs text-red-500">Expiré</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          coupon.is_active && !isExpired(coupon.expires_at)
                            ? 'bg-[#00C48C]/10 text-[#00A67E]'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {coupon.is_active && !isExpired(coupon.expires_at) ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchCouponUsage(coupon.coupon_id)}
                            className="rounded-lg"
                            title="Voir l'historique"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditCoupon(coupon)}
                            className="rounded-lg text-blue-600"
                            title="Modifier"
                            data-testid={`edit-coupon-${coupon.coupon_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleCoupon(coupon.coupon_id)}
                            className={`rounded-lg ${coupon.is_active ? 'text-[#00C48C]' : 'text-slate-400'}`}
                            title={coupon.is_active ? 'Désactiver' : 'Activer'}
                            data-testid={`toggle-coupon-${coupon.coupon_id}`}
                          >
                            {coupon.is_active ? (
                              <ToggleRight className="w-5 h-5" />
                            ) : (
                              <ToggleLeft className="w-5 h-5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon)}
                            className="rounded-lg text-red-500 hover:text-red-700"
                            title={coupon.current_uses > 0 ? 'Impossible (déjà utilisé)' : 'Supprimer'}
                            disabled={deleting === coupon.coupon_id}
                            data-testid={`delete-coupon-${coupon.coupon_id}`}
                          >
                            {deleting === coupon.coupon_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
